import { TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { IOwners } from '../../interfaces/merchant';
import { isUSCountry } from '../../utils/utilities';

export enum OWNER_FORM_FIELDS {
	FIRST_NAME = 'FirstName',
	PERCENTAGE = 'PercentOwnership',
	LAST_NAME = 'LastName',
	EMAIL = 'Email',
	PHONE = 'Phone',
	LINE_1 = 'line1',
	LINE_2 = 'line2',
	CITY = 'city',
	ZIPCODE = 'ZipCode',
	COUNTRY = 'Country',
	STATE = 'State',
	TITLE = 'title',
	BIRTH_DATE = 'dateOfBirth',
	ID = 'identificationNumber',
	ID_FORM = 'individualFormOfID',
	PRINCIPAL_TYPE = 'principalType',
}

export enum OWNER_FORM_FIELDS_NAMES {
	PERCENTAGE = 'OWNER.PERCENTAGE',
	FIRST_NAME = 'NEW_APPLICATION.LABEL.FIRST_NAME',
	LAST_NAME = 'NEW_APPLICATION.LABEL.LAST_NAME',
	EMAIL = 'NEW_APPLICATION.LABEL.EMAIL',
	PHONE = 'NEW_APPLICATION.LABEL.PHONE',
	LINE_1 = 'NEW_APPLICATION.LABEL.LINE_1',
	LINE_1_TOOLTIP_TEXT = 'OWNER.LINE_1_TOOLTIP_TEXT',
	LINE_2 = 'NEW_APPLICATION.LABEL.LINE_2',
	LINE_2_TOOLTIP_TEXT = 'OWNER.LINE_2_TOOLTIP_TEXT',
	CITY = 'NEW_APPLICATION.LABEL.CITY',
	ZIPCODE = 'NEW_APPLICATION.LABEL.ZIPCODE',
	COUNTRY = 'NEW_APPLICATION.LABEL.COUNTRY',
	STATE = 'NEW_APPLICATION.LABEL.STATE',
	TITLE = 'OWNER.TITLE',
	BIRTH_DATE = 'OWNER.BIRTH_DATE',
	ID = 'OWNER.ID',
	ID_FORM = 'OWNER.ID_FORM',
	PRINCIPAL_TYPE = 'OWNER.PRINCIPAL_TYPE',
}

export type OwnerFieldsTypes = {
	[OWNER_FORM_FIELDS.PERCENTAGE]: string | number;
	[OWNER_FORM_FIELDS.FIRST_NAME]: string;
	[OWNER_FORM_FIELDS.LAST_NAME]: string;
	[OWNER_FORM_FIELDS.EMAIL]: string;
	[OWNER_FORM_FIELDS.PHONE]: string;
	[OWNER_FORM_FIELDS.LINE_1]: string;
	[OWNER_FORM_FIELDS.LINE_2]: string;
	[OWNER_FORM_FIELDS.CITY]: string;
	[OWNER_FORM_FIELDS.ZIPCODE]: string;
	[OWNER_FORM_FIELDS.COUNTRY]: string;
	[OWNER_FORM_FIELDS.STATE]: string;
	[OWNER_FORM_FIELDS.TITLE]: string;
	[OWNER_FORM_FIELDS.BIRTH_DATE]: string | Date;
	[OWNER_FORM_FIELDS.ID]: string;
	[OWNER_FORM_FIELDS.ID_FORM]: string;
	[OWNER_FORM_FIELDS.PRINCIPAL_TYPE]: string;
}

export type OwnerFieldReturnTypes = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	percentOwnership: string | number;
	title: string;
	dateOfBirth: Date;
	id?: string;
	individualFormOfID: string;
	identificationNumber: string;
	principalType: string;
	address: {
		line1: string;
		line2: string;
		zipCode: string;
		state: string;
		// stateCode: null;
		city: string;
		country: string;
	}
}

// @ts-ignore
export const getOwnerValidationSchema = (t: TFunction): Yup.SchemaOf<OwnerFieldsTypes> => Yup.object<Shape<OwnerFieldsTypes>>().shape({
  [OWNER_FORM_FIELDS.PERCENTAGE]: Yup.number()
	  .max(100)
    .required(t('Percentage is required')),
	[OWNER_FORM_FIELDS.FIRST_NAME]: Yup.string()
    .required(t('NEW_APPLICATION.VALIDATION.FIRST_NAME_REQUIRED')),
  [OWNER_FORM_FIELDS.LAST_NAME]: Yup.string()
    .required(t('NEW_APPLICATION.VALIDATION.LAST_NAME_REQUIRED')),
  [OWNER_FORM_FIELDS.EMAIL]: Yup.string()
    .email(t('NEW_APPLICATION.VALIDATION.EMAIL_ERROR_TEXT'))
    .required(t('NEW_APPLICATION.VALIDATION.EMAIL_REQUIRED')),
  [OWNER_FORM_FIELDS.PHONE]: Yup.number()
	  .test('is-valid-phone', t('NEW_APPLICATION.VALIDATION.VALID_PHONE'), (value) => {
		  if (!value) return true;
		  const phoneRegex = /^[0-9]{10,15}$/;
		  return phoneRegex.test(value.toString());
	  })
    .typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
    .required(t('NEW_APPLICATION.VALIDATION.PHONE_REQUIRED')),
});

export const initialOwnerValues: OwnerFieldsTypes = {
	[OWNER_FORM_FIELDS.PERCENTAGE]: '',
	[OWNER_FORM_FIELDS.FIRST_NAME]: '',
	[OWNER_FORM_FIELDS.LAST_NAME]: '',
	[OWNER_FORM_FIELDS.EMAIL]: '',
	[OWNER_FORM_FIELDS.PHONE]: '',
	[OWNER_FORM_FIELDS.LINE_1]: '',
	[OWNER_FORM_FIELDS.LINE_2]: '',
	[OWNER_FORM_FIELDS.CITY]: '',
	[OWNER_FORM_FIELDS.ZIPCODE]: '',
	[OWNER_FORM_FIELDS.COUNTRY]: '',
	[OWNER_FORM_FIELDS.STATE]: '',
	[OWNER_FORM_FIELDS.TITLE]: '',
	[OWNER_FORM_FIELDS.BIRTH_DATE]: null,
	[OWNER_FORM_FIELDS.ID]: '',
	[OWNER_FORM_FIELDS.ID_FORM]: '',
	[OWNER_FORM_FIELDS.PRINCIPAL_TYPE]: '',
};

export const getFormattedOwnerFormFields = (formikValues): OwnerFieldReturnTypes => ({
	percentOwnership: formikValues?.[OWNER_FORM_FIELDS.PERCENTAGE],
	firstName: formikValues?.[OWNER_FORM_FIELDS.FIRST_NAME],
	lastName: formikValues?.[OWNER_FORM_FIELDS.LAST_NAME],
	email: formikValues?.[OWNER_FORM_FIELDS.EMAIL],
	phone: formikValues?.[OWNER_FORM_FIELDS.PHONE],
	title: formikValues?.[OWNER_FORM_FIELDS.TITLE],
	dateOfBirth: formikValues?.[OWNER_FORM_FIELDS.BIRTH_DATE],
	identificationNumber: formikValues?.[OWNER_FORM_FIELDS.ID],
	individualFormOfID: formikValues?.[OWNER_FORM_FIELDS.ID_FORM],
	principalType: formikValues?.[OWNER_FORM_FIELDS.PRINCIPAL_TYPE],
	address: {
		line1: formikValues?.[OWNER_FORM_FIELDS.LINE_1] ? formikValues?.[OWNER_FORM_FIELDS.LINE_1] : null,
		line2: formikValues?.[OWNER_FORM_FIELDS.LINE_2],
		zipCode: formikValues?.[OWNER_FORM_FIELDS.ZIPCODE]?.toString(),
		state: isUSCountry(formikValues?.[OWNER_FORM_FIELDS.COUNTRY]) ? formikValues?.[OWNER_FORM_FIELDS.STATE] : null,
		// stateCode: null,
		city: formikValues?.[OWNER_FORM_FIELDS.CITY],
		country: formikValues?.[OWNER_FORM_FIELDS.COUNTRY],
	}
});

export const convertToOwnerInitialValues = (data: IOwners):OwnerFieldsTypes => ({
	[OWNER_FORM_FIELDS.PERCENTAGE]: data?.percentOwnership,
	[OWNER_FORM_FIELDS.FIRST_NAME]: data?.firstName,
	[OWNER_FORM_FIELDS.LAST_NAME]: data?.lastName,
	[OWNER_FORM_FIELDS.EMAIL]: data?.email,
	[OWNER_FORM_FIELDS.PHONE]: data?.phone,
	[OWNER_FORM_FIELDS.LINE_1]: data?.address?.line1,
	[OWNER_FORM_FIELDS.LINE_2]: data?.address?.line2,
	[OWNER_FORM_FIELDS.CITY]: data?.address?.city,
	[OWNER_FORM_FIELDS.ZIPCODE]: data?.address?.zipCode,
	[OWNER_FORM_FIELDS.COUNTRY]: data?.address?.country,
	[OWNER_FORM_FIELDS.STATE]: data?.address?.state,
	[OWNER_FORM_FIELDS.TITLE]: data?.title,
	[OWNER_FORM_FIELDS.BIRTH_DATE]: data?.dateOfBirth ? new Date(data?.dateOfBirth) : null,
	[OWNER_FORM_FIELDS.ID]: data?.identificationNumber,
	[OWNER_FORM_FIELDS.ID_FORM]: data?.individualFormOfID,
	[OWNER_FORM_FIELDS.PRINCIPAL_TYPE]: data?.principalType,
});

